import React, { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Alert, Button, Stack, Typography } from '@mui/material';

import { heatmapSurchargeQueryKeys } from '~/hooks/apis/lere/heatmapSurchargeQueryKeys';
import { useGetHeatmapSurchargeJob } from '~/hooks/apis/lere/useGetHeatmapSurchargeJob';

import { HeatmapSurchargeApplication } from '~/types/heatmapSurcharge';

interface Props {
  jobId: string;
  label: string;
  onStatusChange: (jobId: string, status: HeatmapSurchargeApplication['status']) => void;
}

export default function HeatmapApplicationStatus({ jobId, label, onStatusChange }: Props) {
  const job = useGetHeatmapSurchargeJob(jobId);
  const queryClient = useQueryClient();
  const status = job.data?.status;
  useEffect(() => {
    if (status === 'success' || status === 'failure') {
      onStatusChange(jobId, status);
      void queryClient.invalidateQueries({ queryKey: heatmapSurchargeQueryKeys.calendars() });
    }
  }, [status, jobId, onStatusChange, queryClient]);

  return (
    <Alert
      severity={job.isError || status === 'failure' ? 'warning' : status === 'success' ? 'success' : 'info'}
      action={
        job.isError ? (
          <Button disabled={job.isFetching} onClick={() => job.refetch()}>
            Check status
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={0.5}>
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
        <Typography variant="body2">
          {job.isError
            ? 'Could not confirm the job outcome. Check its status or the Hotels Change Log before submitting again.'
            : status === 'success'
              ? 'The worker completed this application. Displayed prices may take until the next pricing refresh to update.'
              : status === 'failure'
                ? 'The application failed. Some rate plans may already have been updated; review the Hotels Change Log before retrying.'
                : 'Approval queued. Waiting for the pricing worker to finish…'}
        </Typography>
        <Typography variant="caption">Job: {jobId}</Typography>
      </Stack>
    </Alert>
  );
}
